import { find, includes, isFinite, isNil, set, isEmpty } from 'lodash';
import { Bert } from 'meteor/themeteorchef:bert';

import * as layerOps from '../../api/Layers/layer-operations';
import { styleFetchOne } from '../../api/Styles/style-methods-client';
import * as styleOps from '../../api/Styles/style-operations';
import { POINT_TYPES } from '../../api/Styles/Styles';

import addPopupToMapBoxGL from './add-popup-to-mapbox-gl';
import buildGeoImageLayer from './buildGeoImageLayer';
import buildGeoJSONLayer from './buildGeoJSONLayer';
import buildGeoJSONVTLayer from './buildGeoJSONVTLayer';
import buildGeoTiffLayer from './buildGeoTiffLayer';
import buildIndoorGeoJSONLayer from './buildIndoorGeoJSONLayer';
import buildMapBoxGLLayer from './buildMapBoxGLLayer';
import buildMapBoxLayer from './buildMapBoxLayer';
import buildRealTimeLayer from './buildRealTimeLayer';
import buildTileServerLayer from './buildTileServerLayer';
import buildTMSLayer from './buildTMSLayer';
import buildTopoJSONLayer from './buildTopoJSONLayer';
import buildWMSLayer from './buildWMSLayer';
import buildWMTSLayer from './buildWMTSLayer';
import { scaleIconSize } from '../geojson-layer-style';
import getDefaultStyle from '../get-default-style';

const ERROR_MESSAGE = 'No está disponible para este tipo de layer';
const REGEX = /mobile/i;
const ARBITRARY_MAX_ZOOM = 20;

/**
 * Adds the selected layer to the map.
 *
 * @async
 * @param {object} arg - The object argument.
 * @param {boolean} arg.isAdded - Flag to determine if the layer it will be added to the map.
 * @param {object} arg.layer - The layer to draw in the map.
 * @param {object[]} arg.layersTransparency - An array containing the layers that need a different transparency value.
 * @param {object} arg.leafletMap - The leaflet map object.
 * @param {number} arg.zIndex - The zIndex for the leaflet layer.
 */
const addLayerToLeafletMap = async ({
  isAdded,
  layer,
  layersTransparency,
  leafletMap,
  popupProp,
  zIndex
}) => {
  let transparency;
  const layerTransparency = find(layersTransparency, ['id', layerOps.getId(layer)]);
  if (!isNil(layerTransparency)) transparency = layerTransparency.value;

  try {
    let newLayer = '';
    const style = !isEmpty(layer.styleValue)
      ? layer.styleValue
      : (await styleFetchOne(layerOps.getStyleId(layer))) || getDefaultStyle(layer);
    switch (true) {
      case layerOps.isTileServer(layer):
        newLayer = buildTileServerLayer({ layer, style, zIndex });
        if (!isNil(transparency)) set(newLayer, ['options', 'opacity'], transparency);
        break;
      case layerOps.isTMS(layer):
        newLayer = buildTMSLayer({ layer, style, zIndex });
        if (!isNil(transparency)) set(newLayer, ['options', 'opacity'], transparency);
        break;
      case layerOps.isWMS(layer):
        newLayer = buildWMSLayer({ layer, style, zIndex });
        if (!isNil(transparency)) set(newLayer, ['options', 'opacity'], transparency);
        break;
      case layerOps.isWMTS(layer):
        newLayer = buildWMTSLayer({ layer, style, zIndex });
        if (!isNil(transparency)) set(newLayer, ['options', 'opacity'], transparency);
        break;
      case layerOps.isGeoImage(layer):
        newLayer = buildGeoImageLayer({ layer, style, zIndex });
        if (!isNil(transparency)) set(newLayer, ['options', 'opacity'], transparency);
        break;
      case layerOps.isGeoTiff(layer):
        newLayer = buildGeoTiffLayer({ layer, style, zIndex });
        if (!isNil(transparency)) set(newLayer, ['options', 'opacity'], transparency);
        break;
      case layerOps.isGeoJSONApiBased(layer):
        if (!isNil(transparency)) set(style, ['geojsonStyle', 'styleData', 'alpha'], transparency);
        newLayer = await buildGeoJSONLayer({ layer, style, popupProp, isPartOfMap: true, zIndex });
        break;
      case layerOps.isGeoJSONVT(layer):
        if (!isNil(transparency)) set(style, ['geojsonStyle', 'styleData', 'alpha'], transparency);
        newLayer = await buildGeoJSONVTLayer({
          isPartOfMap: true,
          layer,
          popupProp,
          style,
          zIndex
        });
        break;
      case layerOps.isIndoor(layer):
        if (!isNil(transparency)) set(style, ['geojsonStyle', 'styleData', 'alpha'], transparency);
        newLayer = await buildIndoorGeoJSONLayer({
          layer,
          style,
          isPartOfMap: true,
          leafletMap,
          zIndex
        });
        break;
      case layerOps.isMapBox(layer):
        if (!isNil(transparency)) set(style, ['geojsonStyle', 'styleData', 'alpha'], transparency);
        newLayer = await buildMapBoxLayer({ isPartOfMap: true, layer, popupProp, style, zIndex });
        break;
      case layerOps.isAnnotationsLayer(layer):
        if (!isNil(transparency)) set(style, ['geojsonStyle', 'styleData', 'alpha'], transparency);
        newLayer = await buildGeoJSONLayer({ isPartOfMap: true, layer, popupProp, style, zIndex });
        break;
      case layerOps.isMapBoxGL(layer):
        newLayer = buildMapBoxGLLayer({ layer, style, zIndex });
        if (!isNil(transparency)) set(newLayer, ['options', 'opacity'], transparency);
        break;
      case layerOps.isAnnotationsRealTime(layer):
      case layerOps.isRealTime(layer):
        if (!isNil(transparency)) set(style, ['geojsonStyle', 'styleData', 'alpha'], transparency);
        newLayer = await buildRealTimeLayer({ layer, style, isPartOfMap: true, zIndex });
        break;
      case layerOps.isTopoJSON(layer):
        if (!isNil(transparency)) set(style, ['geojsonStyle', 'styleData', 'alpha'], transparency);
        newLayer = await buildTopoJSONLayer({ layer, style, isPartOfMap: true, zIndex });
        break;
      default:
        Bert.alert('Wrong Layer data', 'danger');
        break;
    }
    newLayer.ipsilumId = layerOps.getId(layer);
    newLayer.show = isAdded;
    newLayer.options.maxZoom = layerOps.getMaxZoom(layer);
    newLayer.options.minZoom = layerOps.getMinZoom(layer);
    if (isAdded) {
      leafletMap.addLayer(newLayer);
      // if (newLayer._map._layersMaxZoom !== layerOps.getMaxZoom(layer)) {
      //   leafletMap.options.minZoom = layerOps.getMinZoom(layer);
      //   leafletMap.options.maxZoom = layerOps.getMaxZoom(layer);
      // }
      if (layerOps.isMapBoxGL(layer))
        addPopupToMapBoxGL({
          layer,
          leafletMap,
          newLayerToDraw: newLayer,
          popupProp,
          style
        });
    }

    if (
      navigator.userAgent.match(REGEX) &&
      (layerOps.isGeoJSONVT(layer) || layerOps.isMapBox(layer))
    )
      leafletMap.on('zoomanim', () => newLayer.redraw());

    if (
      styleOps.isScaledSymbology(style) &&
      includes([POINT_TYPES.ICON, POINT_TYPES.MARKER], styleOps.getPointType(style))
    ) {
      let fromZoom = leafletMap.getZoom();
      const maxZoom = isFinite(leafletMap.getMaxZoom())
        ? leafletMap.getMaxZoom()
        : ARBITRARY_MAX_ZOOM;
      const initialScale = leafletMap.getZoomScale(fromZoom, maxZoom);
      leafletMap.on('zoomstart', event => {
        fromZoom = event.target.getZoom();
      });
      const isMarker = styleOps.getPointType(style) === POINT_TYPES.MARKER;
      scaleIconSize({ newLayerToDraw: newLayer, scale: initialScale, isMarker });
      leafletMap.on('zoomend', () => {
        const toZoom = leafletMap.getZoom();
        const scale = leafletMap.getZoomScale(toZoom, fromZoom);
        scaleIconSize({ newLayerToDraw: newLayer, scale, isMarker });
      });
    }
    return newLayer;
  } catch (error) {
    throw new Error(`${ERROR_MESSAGE} ${layerOps.getName(layer)}`);
  }
};

export default addLayerToLeafletMap;
